/**
 * Regrouper une série quotidienne par semaine, mois, trimestre ou année.
 *
 * ## Pourquoi ce fichier existe
 *
 * Cette logique vivait en **trois copies divergentes** — `GA4DashboardView` (l. 26 et 102),
 * `MatomoDashboardView`, `PianoDashboardView` — et les divergences n'étaient pas cosmétiques :
 * la même grandeur s'y appelait `uniqueVisitors`, `visitors` ou `activeUsers` selon le fichier,
 * et deux des trois ignoraient `actionsPerVisit`. Le dashboard PrestaShop en aurait fait une
 * quatrième ; c'est ce qu'on évite ici.
 *
 * ⚠️ **Les trois vues existantes ne sont pas migrées aujourd'hui.** Les y faire passer demande de
 * vérifier chacune de leurs divergences, et mêler cette reprise à la création d'un écran rendrait
 * les deux irrelisibles. Ce module est écrit pour les accueillir : c'est le premier appelant qui
 * fixe le contrat, pas le dernier.
 *
 * ## Le contrat : ce qui s'additionne, ce qui se moyenne, ce qui se recalcule
 *
 * Les trois copies codaient leur vocabulaire en dur, ce qui les rendait inutilisables ailleurs.
 * Ici, l'appelant **déclare** le traitement de chaque champ :
 *
 * - `sommes` — les grandeurs qui s'additionnent : un chiffre d'affaires, des commandes, des
 *   visites.
 * - `moyennesPonderees` — celles qui n'ont de sens que pondérées : un taux de rebond sur une
 *   semaine n'est pas la moyenne de ses sept taux quotidiens, c'est la moyenne pondérée par le
 *   trafic de chaque jour. Le poids est nommé par `poids`.
 * - `ratios` — celles qui se **recalculent** sur les sommes de la période. ⚠️ Moyenner un panier
 *   moyen jour par jour donne un nombre qui n'est le panier moyen de personne : c'est le CA de la
 *   semaine divisé par ses commandes qu'il faut.
 *
 * ## ⚠️ Un écart assumé avec les trois copies : les dates sont lues en UTC
 *
 * `new Date('2026-09-01')` rend minuit **UTC**, mais `getDay()` et `getDate()` lisent l'heure
 * **locale** : à l'ouest de Greenwich, le 1er septembre se présente comme le 31 août, et une
 * journée bascule dans la semaine ou le mois précédent. Les trois copies portent ce défaut ;
 * celle-ci lit en UTC d'un bout à l'autre. C'est une correction, pas une divergence — et c'est
 * dit ici pour que la migration des trois autres ne la prenne pas pour une régression.
 */

/**
 * La clé de période d'une journée.
 *
 * @param {string} dateIso `AAAA-MM-JJ`
 * @param {string} granularite `day` | `week` | `month` | `quarter` | `year`
 */
export function clePeriode(dateIso, granularite) {
  if (typeof dateIso !== 'string' || dateIso.length < 10) return dateIso;

  const date = new Date(`${dateIso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return dateIso;

  switch (granularite) {
    case 'week': {
      // Lundi de la semaine. `getUTCDay()` rend 0 le dimanche, qu'il faut reculer de six jours.
      const jour = date.getUTCDay();
      const lundi = new Date(date);
      lundi.setUTCDate(date.getUTCDate() - jour + (jour === 0 ? -6 : 1));
      return lundi.toISOString().slice(0, 10);
    }
    case 'month':
      return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
    case 'quarter':
      return `${date.getUTCFullYear()}-T${Math.floor(date.getUTCMonth() / 3) + 1}`;
    case 'year':
      return `${date.getUTCFullYear()}`;
    default:
      return dateIso;
  }
}

/**
 * Regroupe une série quotidienne selon une granularité.
 *
 * `granularite: 'day'` rend la série telle quelle — c'est le cas le plus fréquent, et il ne doit
 * rien coûter.
 *
 * @param {Array<object>} serie journées, chacune portant au moins `date`
 * @param {string} granularite
 * @param {object} contrat
 * @param {string[]} [contrat.sommes]
 * @param {string[]} [contrat.moyennesPonderees]
 * @param {Object<string, [string, string, number?]>} [contrat.ratios]
 *   `{ panierMoyen: ['caHt', 'commandes'] }` — numérateur, dénominateur, et un facteur optionnel
 *   (100 pour un pourcentage). Un dénominateur nul rend `null`, jamais zéro : « aucune commande »
 *   n'est pas « un panier moyen de 0 € ».
 * @param {string} [contrat.poids] le champ qui pondère les moyennes
 */
export function agregerParGranularite(serie, granularite, {
  sommes = [], moyennesPonderees = [], ratios = {}, poids = null
} = {}) {
  if (!Array.isArray(serie) || serie.length === 0) return [];
  if (!granularite || granularite === 'day') return serie;

  const groupes = new Map();

  for (const jour of serie) {
    if (!jour || !jour.date) continue;
    const cle = clePeriode(jour.date, granularite);

    if (!groupes.has(cle)) {
      groupes.set(cle, {
        date: cle,
        __poids: 0,
        __ponderees: Object.fromEntries(moyennesPonderees.map(c => [c, 0])),
        ...Object.fromEntries(sommes.map(c => [c, 0]))
      });
    }
    const groupe = groupes.get(cle);

    for (const champ of sommes) groupe[champ] += Number(jour[champ]) || 0;

    const p = poids ? (Number(jour[poids]) || 0) : 1;
    groupe.__poids += p;
    for (const champ of moyennesPonderees) {
      groupe.__ponderees[champ] += (Number(jour[champ]) || 0) * p;
    }
  }

  return [...groupes.values()]
    .map(({ __poids, __ponderees, ...groupe }) => {
      for (const champ of moyennesPonderees) {
        groupe[champ] = __poids > 0 ? __ponderees[champ] / __poids : null;
      }
      for (const [nom, [numerateur, denominateur, facteur = 1]] of Object.entries(ratios)) {
        const bas = Number(groupe[denominateur]) || 0;
        // ⚠️ `null` et non 0 : une période sans commande n'a pas un panier moyen nul, elle n'en
        // a pas. Combler à zéro ferait plonger une courbe pour une absence de donnée.
        groupe[nom] = bas > 0 ? (Number(groupe[numerateur]) || 0) / bas * facteur : null;
      }
      return groupe;
    })
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));
}

export default agregerParGranularite;
